import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, X, LayoutDashboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';

const AdminAccess = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  // Only show admin access button in preview environments
  const isPreview = API_URL.includes('preview.emergentagent.com') || window.location.hostname.includes('preview.emergentagent.com');
  
  // Secret PIN for admin access
  const ADMIN_PIN = '2481';

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin === ADMIN_PIN) {
      navigate('/admin');
      setShowPin(false);
      setPin('');
    } else {
      setError(true);
      setTimeout(() => setError(false), 1000);
      setPin('');
    }
  };

  // Don't render anything in production
  if (!isPreview) {
    return null;
  }

  return (
    <>
      {/* Hidden Admin Tab - Bottom Left */}
      <div
        className="fixed bottom-6 left-6 z-50"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <motion.button
          onClick={() => setShowPin(true)}
          className="relative group"
          initial={{ opacity: 0.1 }}
          animate={{ opacity: isHovered ? 1 : 0.1 }}
          whileHover={{ scale: 1.1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Outer glow on hover */}
          <motion.div
            className="absolute inset-0 bg-[#D4AF37]/20 rounded-full blur-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
          />
          
          {/* Button */}
          <div className={`relative w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 ${
            isHovered 
              ? 'bg-[#D4AF37]/10 border-[#D4AF37]/50' 
              : 'bg-transparent border-white/5'
          }`}>
            <Settings className={`w-4 h-4 transition-colors duration-300 ${
              isHovered ? 'text-[#D4AF37]' : 'text-white/10'
            }`} />
          </div>
        </motion.button>

        {/* Tooltip */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="absolute left-14 top-1/2 -translate-y-1/2 whitespace-nowrap"
            >
              <span className="text-[#D4AF37] text-xs font-mono">Admin</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* PIN Modal */}
      <AnimatePresence>
        {showPin && (
          <motion.div
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPin(false)}
          >
            <motion.div
              className="bg-[#111111] border border-white/10 p-8 w-full max-w-sm"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setShowPin(false)}
                className="absolute top-4 right-4 text-white/30 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                  <LayoutDashboard className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-mono text-lg font-bold uppercase">Admin Access</h3>
                <p className="text-white/50 text-sm mt-2">Enter PIN to continue</p>
              </div>

              {/* PIN Input */}
              <form onSubmit={handlePinSubmit}>
                <div className="mb-6">
                  <input
                    type="password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="••••"
                    maxLength={4}
                    className={`w-full bg-[#0A0A0A] border text-center text-2xl tracking-[0.5em] py-4 font-mono focus:outline-none transition-colors ${
                      error 
                        ? 'border-red-500 text-red-500' 
                        : 'border-white/10 focus:border-[#D4AF37] text-white'
                    }`}
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#D4AF37] text-black py-3 font-mono text-sm uppercase tracking-wider hover:bg-[#D4AF37]/90 transition-colors"
                >
                  Access Panel
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminAccess;
